// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
// import { LinkPresentationProps } from "@insite/mobius/Link"; // Starter Custom Code
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React, { FC, useState } from "react"; // Greif Custom Code
import { css } from "styled-components";

/* Starter Custom Code - Start */
import EditNotesFormModal from "@store/Library/Components/Toolkit/EditNotesFormModal";
import ArrowiOSDownward from "@store/Library/Components/Toolkit/Icons/ArrowiOSDownward";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
// import { productLineProps } from "@store/Themes/common/props/productLine"; // Greif Custom Code
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
// import { spacingStyles } from "@toolkit/Themes/common/mixins"; // Greif Custom Code
// import React, { FC, useRef, useState } from "react"; // Greif Custom Code
/* Starter Custom Code - End */

/* Greif Custom Code - Start */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ArrowiOSUpward from "@store/Library/Components/Toolkit/Icons/ArrowiOSUpward";
import { productLineProps } from "@store/Themes/common/props/productLine";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
/* Greif Custom Code - End */

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    // Greif
    isLineNotesOpen?: boolean;
    orderLineNotesMaxLength?: number;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    // Starter Custom Code
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    editNotesLink?: LinkPresentationProps;
    addNotesLink?: LinkPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: productLineProps.notesWrapper,
    editableNotesTextArea: {
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                ${spacingStyles(pxToRem(5), null)}
            `,
        },
    },
    linkWrapper: {
        css: css`
            display: flex;
        `,
    },
    link: linkGetActionLinkProps(ArrowiOSDownward, "right"),
    readOnlyNotesText: {
        wrapper: {
            css: spacingStyles(pxToRem(8), null),
        },
    },
    editNotesLink: productLineProps.editNotesLink,
    addNotesLink: productLineProps.addNotesLink,
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
    // Greif
    isLineNotesOpen,
    orderLineNotesMaxLength,
}) => {
    const [isNotesFormModalOpen, setIsNotesFormModalOpen] = useState(false); // Starter Custom Code
    const [showLineNotes, setShowLineNotes] = useState(isLineNotesOpen); // Greif
    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNotes);
    };

    const notesLinkRef = useRef<HTMLButtonElement>(null); // Starter Custom Code

    /* Greif Custom Code - Start */
    const { isUpdatingNotes } = useSelector(
        (state: ApplicationState) => state.greif.pages.reviewAndSubmit.splitOrderCartLine,
    );

    useEffect(() => {
        if (!isLineNotesOpen) {
            cartLine.notes !== "" ? setShowLineNotes(true) : setShowLineNotes(false);
        }
    }, [cartLine.notes]);
    /* Greif Custom Code - End */

    const [notes, setNotes] = useState(cartLine.notes);

    /* Greif Custom Code - Start */
    const [notesLength, setNotesLength] = useState<number>(cartLine.notes.length);
    const hintText =
        orderLineNotesMaxLength && notesLength > orderLineNotesMaxLength * 0.8
            ? translate(`{0} of {1} characters`, String(notesLength), String(orderLineNotesMaxLength))
            : null;
    /* Greif Custom Code - End */

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
        setNotesLength(event.currentTarget.value.length); // Greif
    };

    const blurHandler = () => {
        onNotesChange(notes);
    };

    /* Starter Custom Code - Start */
    const openNotesFormModal = () => {
        setIsNotesFormModalOpen(true);
    };

    const closeNotesFormModal = () => {
        setIsNotesFormModalOpen(false);
    };

    const onLineNoteSave = (notes: string) => {
        onNotesChange(notes);
        closeNotesFormModal();
        notesLinkRef.current?.focus();
    };
    /* Starter Custom Code - End */

    const heading = cartLine.notes ? translate("Edit Notes") : translate("Add Notes"); // Starter Custom Code

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));
    const link = linkGetActionLinkProps(showLineNotes ? ArrowiOSDownward : ArrowiOSUpward, "right"); // Greif

	/* Greif keeps old widget due to custom */
    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {/* Starter Custom Code */}
                <StyledWrapper {...styles.linkWrapper}>
                    <Link
                        {...link} // Greif
                        aria-expanded={showLineNotes}
                        onClick={lineNotesClickHandler}
                        data-test-selector="cartline_notesShowHideButton"
                        ref={notesLinkRef}
                    >
                        {translate(heading || "")}
                    </Link>
                </StyledWrapper>
                {/* Starter Custom Code */}
                {showLineNotes && (
                    <LoadingOverlay loading={isUpdatingNotes} /* Greif Custom Code - Overlay */>
                        <TextArea
                            {...styles.editableNotesTextArea}
                            value={notes}
                            id={`notes-${cartLine.id}`}
                            disabled={!cart.canModifyOrder}
                            onChange={changeHandler}
                            onBlur={blurHandler}
                            data-test-selector="cartline_notes"
                            // Starter Custom Code
                            placeholder={translate("Enter Line Notes")}
                            aria-label={`${translate("Enter Line Notes")}${translate(":")} ${
                                cartLine.shortDescription
                            }`}
                            maxLength={orderLineNotesMaxLength} // Greif
                            hint={hintText} // Greif
                        />
                    </LoadingOverlay>
                )}
            </StyledWrapper>
        );
    }

    if (!cartLine.notes) {
        return null;
    }

    return (
        <SmallHeadingAndText
            extendedStyles={styles.readOnlyNotesText /* Starter Custom Code */}
            heading={label || translate("Item Notes")}
            text={cartLine.notes}
        />
    );
};

export default withCartLine(CartLineNotes);
